"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useMemo } from "react";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { useLocale } from "@/lib/labels/LabelsProvider";
import { SITE_TYPE_FOOTBALL_COMPETITION } from "@/config/siteTypes";
import { createUrlBuilder, type UrlBuilder } from "./createUrlBuilder";

// ---------------------------------------------------------------------------
// Optional context – allows ancestor components (e.g. competition pages on
// news-blog sites) to supply a competitionId that isn't in tenant settings.
// ---------------------------------------------------------------------------

interface NavigationContextValue {
  competitionId?: string;
}
const NavigationContext = createContext<NavigationContextValue>({});
interface NavigationProviderProps {
  /** Competition ID to make available to all descendant useNavigation() calls. */
  competitionId: string;
  children: React.ReactNode;
}

/**
 * Provides a competitionId to all descendant `useNavigation()` calls.
 *
 * Use this on pages where the competition ID comes from the URL rather than
 * from tenant settings (e.g. `/football/competitions/:id/…` on news-blog sites).
 *
 * On `football-competition-site` tenants the ID is already in TenantSettings,
 * so this wrapper is not needed.
 */
export function NavigationProvider(t0) {
  const $ = _c(5);
  const {
    competitionId,
    children
  } = t0;
  let t1;
  if ($[0] !== competitionId) {
    t1 = {
      competitionId
    };
    $[0] = competitionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  let t2;
  if ($[2] !== children || $[3] !== value) {
    t2 = <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
    $[2] = children;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Client hook that returns a memoized UrlBuilder for the current tenant and locale.
 *
 * Competition ID resolution:
 *   - `football-competition-site`: use `TenantSettings.competitionId`, falling back to context
 *   - `news-blog` (and anything else): only use `NavigationContext.competitionId` — the tenant
 *     default is meaningless here because the user can be on any competition's page
 *
 * Usage:
 * ```tsx
 * "use client";
 * import { useNavigation } from "@/lib/navigation/NavigationProvider";
 *
 * function MyComponent() {
 *     const nav = useNavigation();
 *     return <a href={nav.standings()}>Standings</a>;
 * }
 * ```
 */
export function useNavigation() {
  const $ = _c(5);
  const {
    siteType,
    competitionId: tenantCompetitionId,
    isMultiLang
  } = useTenantSettings();
  const {
    competitionId: contextCompetitionId
  } = useContext(NavigationContext);
  const locale = useLocale();
  const competitionId = siteType === SITE_TYPE_FOOTBALL_COMPETITION ? tenantCompetitionId ?? contextCompetitionId : contextCompetitionId;
  let t0;
  if ($[0] !== competitionId || $[1] !== isMultiLang || $[2] !== locale || $[3] !== siteType) {
    t0 = createUrlBuilder(siteType, locale, isMultiLang, competitionId);
    $[0] = competitionId;
    $[1] = isMultiLang;
    $[2] = locale;
    $[3] = siteType;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
}